/**
 * VisualKeyboard - Clavier piano visuel pour jouer les synthés en temps réel
 *
 * Features:
 * - Clavier visuel cliquable (2 octaves complètes)
 * - Mapping clavier PC
 * - Sélecteur d'instrument
 * - Contrôle d'octave
 */

export class VisualKeyboard {
    constructor(instrumentsMap) {
        this.instrumentsMap = instrumentsMap;
        this.container = null;
        this.baseOctave = 3;
        this.selectedInstrument = null;
        this.selectedInstrumentId = 'jupiter8';
        this.activeNotes = new Map();
        this.activeKeys = new Set();
        this.isCollapsed = localStorage.getItem('keyboard_collapsed') === 'true';

        // Configuration des instruments avec leur type d'API
        this.instruments = [
            { id: 'tb303_1', name: 'TB-303 #1', color: '#00ff88', type: 'tb303' },
            { id: 'tb303_2', name: 'TB-303 #2', color: '#00ff88', type: 'tb303' },
            { id: 'minimoog', name: 'Minimoog', color: '#d4a574', type: 'mono' },
            { id: 'ms20', name: 'MS-20', color: '#a8b8c8', type: 'mono' },
            { id: 'jupiter8', name: 'Jupiter-8', color: '#e85d04', type: 'poly' },
            { id: 'arp2600', name: 'ARP 2600', color: '#3af', type: 'arp' },
            { id: 'juno106', name: 'Juno-106', color: '#00bcd4', type: 'poly' },
            { id: 'sh101', name: 'SH-101', color: '#8bc34a', type: 'poly' }
        ];

        // Mapping clavier PC vers notes relatives (0-23 pour 2 octaves)
        // Rangée du bas pour octave 1, rangée du milieu pour octave 2
        this.keyMap = {
            // Octave 1 - Rangée WXCVBN (touches blanches)
            'KeyW': 0,   // C
            'KeyX': 2,   // D
            'KeyC': 4,   // E
            'KeyV': 5,   // F
            'KeyB': 7,   // G
            'KeyN': 9,   // A
            'Comma': 11, // B

            // Octave 1 - Touches noires (au-dessus)
            'KeyS': 1,   // C#
            'KeyD': 3,   // D#
            'KeyG': 6,   // F#
            'KeyH': 8,   // G#
            'KeyJ': 10,  // A#

            // Octave 2 - Rangée QSDFGHJ... (décalée)
            'KeyI': 12,  // C
            'KeyO': 14,  // D
            'KeyP': 16,  // E

            // Octave 2 - Touches noires
            'Digit9': 13,  // C#
            'Digit0': 15,  // D#
        };

        this._boundKeyDown = this._onKeyDown.bind(this);
        this._boundKeyUp = this._onKeyUp.bind(this);
    }

    render(container) {
        this.container = container;
        container.innerHTML = `
            <div class="machine keyboard-machine ${this.isCollapsed ? 'collapsed' : ''}" id="keyboard_machine">
                <div class="machine-title">
                    <button class="collapse-btn" id="keyboard_collapse" title="Réduire/Agrandir">${this.isCollapsed ? '▶' : '▼'}</button>
                    KEYBOARD <span class="brand">Live Play</span>
                    <div class="keyboard-controls">
                        <div class="octave-control">
                            <button class="octave-btn" id="octaveDown">−</button>
                            <span class="octave-display" id="octaveDisplay">C${this.baseOctave}</span>
                            <button class="octave-btn" id="octaveUp">+</button>
                        </div>
                        <div class="instrument-select">
                            <label>INSTRUMENT</label>
                            <select id="keyboardInstrument">
                                ${this.instruments.map(inst =>
                                    `<option value="${inst.id}" ${inst.id === this.selectedInstrumentId ? 'selected' : ''}>${inst.name}</option>`
                                ).join('')}
                            </select>
                        </div>
                    </div>
                </div>
                <div class="machine-content ${this.isCollapsed ? 'hidden' : ''}" id="keyboard_content">
                    <div class="visual-keyboard">
                        <div class="piano-container">
                            <div class="piano-keys" id="pianoKeys">
                                ${this._generateKeysHTML()}
                            </div>
                        </div>
                        <div class="keyboard-hint">
                            Octave 1: <kbd>W</kbd><kbd>X</kbd><kbd>C</kbd><kbd>V</kbd><kbd>B</kbd><kbd>N</kbd> (blanches)
                            <kbd>S</kbd><kbd>D</kbd><kbd>G</kbd><kbd>H</kbd><kbd>J</kbd> (noires) |
                            Octave 2: <kbd>I</kbd><kbd>O</kbd><kbd>P</kbd>
                        </div>
                    </div>
                </div>
            </div>
        `;

        this._setupEventListeners();
        this._updateSelectedInstrument();
    }

    _generateKeysHTML() {
        // 2 octaves complètes
        const whiteKeys = [
            { note: 0, name: 'C', label: 'W' },
            { note: 2, name: 'D', label: 'X' },
            { note: 4, name: 'E', label: 'C' },
            { note: 5, name: 'F', label: 'V' },
            { note: 7, name: 'G', label: 'B' },
            { note: 9, name: 'A', label: 'N' },
            { note: 11, name: 'B', label: ',' },
            { note: 12, name: 'C', label: 'I' },
            { note: 14, name: 'D', label: 'O' },
            { note: 16, name: 'E', label: 'P' },
            { note: 17, name: 'F', label: '' },
            { note: 19, name: 'G', label: '' },
            { note: 21, name: 'A', label: '' },
            { note: 23, name: 'B', label: '' },
        ];

        const blackKeys = [
            { note: 1, label: 'S', pos: 0 },
            { note: 3, label: 'D', pos: 1 },
            { note: 6, label: 'G', pos: 3 },
            { note: 8, label: 'H', pos: 4 },
            { note: 10, label: 'J', pos: 5 },
            { note: 13, label: '9', pos: 7 },
            { note: 15, label: '0', pos: 8 },
            { note: 18, label: '', pos: 10 },
            { note: 20, label: '', pos: 11 },
            { note: 22, label: '', pos: 12 },
        ];

        // Générer touches blanches
        let whiteHTML = whiteKeys.map(key =>
            '<div class="white-key" data-note="' + key.note + '">' +
            '<span class="key-label">' + key.name + '</span>' +
            (key.label ? '<span class="key-binding">' + key.label + '</span>' : '') +
            '</div>'
        ).join('');

        // Générer touches noires
        let blackHTML = '<div class="black-keys-container">';
        blackKeys.forEach(key => {
            const left = (key.pos + 1) * 42 - 14;
            blackHTML += '<div class="black-key" data-note="' + key.note + '" style="left:' + left + 'px">' +
                (key.label ? '<span class="key-binding">' + key.label + '</span>' : '') +
                '</div>';
        });
        blackHTML += '</div>';

        return whiteHTML + blackHTML;
    }

    _setupEventListeners() {
        // Collapse button
        const collapseBtn = document.getElementById('keyboard_collapse');
        if (collapseBtn) {
            collapseBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.toggleCollapse();
            });
        }

        document.addEventListener('keydown', this._boundKeyDown);
        document.addEventListener('keyup', this._boundKeyUp);

        const pianoKeys = document.getElementById('pianoKeys');
        if (!pianoKeys) return;

        // Mouse events
        pianoKeys.addEventListener('mousedown', (e) => {
            const key = e.target.closest('.white-key, .black-key');
            if (key) {
                e.preventDefault();
                const note = parseInt(key.dataset.note);
                this._noteOn(note);
                key.classList.add('active');
            }
        });

        pianoKeys.addEventListener('mouseup', () => this._allNotesOff());
        pianoKeys.addEventListener('mouseleave', () => this._allNotesOff());

        // Touch events
        pianoKeys.addEventListener('touchstart', (e) => {
            e.preventDefault();
            for (const touch of e.changedTouches) {
                const key = document.elementFromPoint(touch.clientX, touch.clientY)?.closest('.white-key, .black-key');
                if (key) {
                    const note = parseInt(key.dataset.note);
                    this._noteOn(note);
                    key.classList.add('active');
                }
            }
        }, { passive: false });

        pianoKeys.addEventListener('touchend', () => this._allNotesOff());

        // Octave controls
        document.getElementById('octaveDown')?.addEventListener('click', () => this._changeOctave(-1));
        document.getElementById('octaveUp')?.addEventListener('click', () => this._changeOctave(1));

        // Instrument selector
        document.getElementById('keyboardInstrument')?.addEventListener('change', (e) => {
            this.selectedInstrumentId = e.target.value;
            this._updateSelectedInstrument();
        });
    }

    _updateSelectedInstrument() {
        this.selectedInstrument = this.instrumentsMap[this.selectedInstrumentId];
        this.instrumentType = this.instruments.find(i => i.id === this.selectedInstrumentId)?.type || 'poly';

        const config = this.instruments.find(i => i.id === this.selectedInstrumentId);
        if (config) {
            document.documentElement.style.setProperty('--keyboard-accent', config.color);
        }
    }

    _onKeyDown(e) {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA') {
            return;
        }

        const relativeNote = this.keyMap[e.code];
        if (relativeNote !== undefined && !this.activeKeys.has(e.code)) {
            e.preventDefault();
            this.activeKeys.add(e.code);
            this._noteOn(relativeNote);

            const keyEl = document.querySelector(`[data-note="${relativeNote}"]`);
            if (keyEl) keyEl.classList.add('active');
        }
    }

    _onKeyUp(e) {
        const relativeNote = this.keyMap[e.code];
        if (relativeNote !== undefined && this.activeKeys.has(e.code)) {
            this.activeKeys.delete(e.code);
            this._noteOff(relativeNote);

            const keyEl = document.querySelector(`[data-note="${relativeNote}"]`);
            if (keyEl) keyEl.classList.remove('active');
        }
    }

    _noteOn(relativeNote) {
        if (!this.selectedInstrument) return;

        const midiNote = (this.baseOctave + 1) * 12 + relativeNote;
        if (this.activeNotes.has(midiNote)) return;

        const noteName = this._midiToNoteName(midiNote);
        this.activeNotes.set(midiNote, true);

        switch (this.instrumentType) {
            case 'tb303':
                if (typeof this.selectedInstrument.triggerNote === 'function') {
                    this.selectedInstrument.triggerNote(noteName, { duration: '8n' });
                }
                break;

            case 'arp':
                if (typeof this.selectedInstrument.triggerAttackRelease === 'function') {
                    this.selectedInstrument.triggerAttackRelease(noteName, '8n');
                }
                break;

            case 'mono':
            case 'poly':
            default:
                if (typeof this.selectedInstrument.triggerAttack === 'function') {
                    this.selectedInstrument.triggerAttack(noteName);
                }
                break;
        }
    }

    _noteOff(relativeNote) {
        if (!this.selectedInstrument) return;

        const midiNote = (this.baseOctave + 1) * 12 + relativeNote;
        if (!this.activeNotes.has(midiNote)) return;

        this.activeNotes.delete(midiNote);
        const noteName = this._midiToNoteName(midiNote);

        switch (this.instrumentType) {
            case 'tb303':
            case 'arp':
                break;

            case 'mono':
                if (typeof this.selectedInstrument.triggerRelease === 'function') {
                    this.selectedInstrument.triggerRelease();
                }
                break;

            case 'poly':
            default:
                if (typeof this.selectedInstrument.triggerRelease === 'function') {
                    this.selectedInstrument.triggerRelease(noteName);
                }
                break;
        }
    }

    _allNotesOff() {
        this.activeNotes.forEach((_, midiNote) => {
            const relativeNote = midiNote - (this.baseOctave + 1) * 12;
            this._noteOff(relativeNote);
        });
        this.activeNotes.clear();

        document.querySelectorAll('.white-key.active, .black-key.active').forEach(el => {
            el.classList.remove('active');
        });
    }

    _midiToNoteName(midiNote) {
        const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
        const octave = Math.floor(midiNote / 12) - 1;
        const note = notes[midiNote % 12];
        return `${note}${octave}`;
    }

    _changeOctave(delta) {
        const newOctave = this.baseOctave + delta;
        if (newOctave >= 1 && newOctave <= 6) {
            this._allNotesOff();
            this.baseOctave = newOctave;
            const display = document.getElementById('octaveDisplay');
            if (display) display.textContent = `C${this.baseOctave}`;
        }
    }

    toggleCollapse() {
        this.isCollapsed = !this.isCollapsed;
        localStorage.setItem('keyboard_collapsed', this.isCollapsed);

        const machine = document.getElementById('keyboard_machine');
        const content = document.getElementById('keyboard_content');
        const btn = document.getElementById('keyboard_collapse');

        if (this.isCollapsed) {
            machine.classList.add('collapsed');
            content.classList.add('hidden');
            btn.textContent = '▶';
        } else {
            machine.classList.remove('collapsed');
            content.classList.remove('hidden');
            btn.textContent = '▼';
        }
    }

    destroy() {
        document.removeEventListener('keydown', this._boundKeyDown);
        document.removeEventListener('keyup', this._boundKeyUp);
        this._allNotesOff();
    }
}
